///
/// This store is for managing locations assigned to a datasheet
/// It is NOT used for managing the display of those assignments
/// when creating new observations.
///
/// The locations and assignments are merged into a single object
/// keyed by location @id to prevent duplicates.
///

import fetch from "../../../utils/fetch";
import Vue from "vue";

const getInitialState = () => {
  return {
    // Status information
    locationsIsLoading: false,
    assignmentsIsLoading: false,

    message: null,
    messageType: null,

    // Filter Data
    projectId: null,
    datasheetId: null,

    searchTerm: null,

    // Results
    // A Key value store might be useful for ensuring there are no duplicate locations across searches
    locationsObj: {},

    locationsTotal: null,
    assignmentsTotal: null,
  };
};
const state = getInitialState();
const getters = {
  // Status
  isLoading: (state) => state.locationsIsLoading || state.assignmentsIsLoading,
  fetchedCount: (state, getters) => getters.allLocations.length,
  fetchedPercent: (state, getters) => {
    if (!getters.allLocations.length) return 0;
    return Math.round(
      (getters.fetchedCount / getters.allLocations.length) * 100
    );
  },
  // Results
  allLocations: (state) => {
    return Object.values(state.locationsObj);
  },
  searchFilteredLocations: (state, getters) => {
    if (!state.searchTerm) return getters.allLocations;
    return getters.allLocations.filter((location) =>
      location.name.toLowerCase().includes(state.searchTerm.toLowerCase())
    );
  },
};

const t = {
  LOCATIONS_ASSIGNMENTS_RESET: "LOCATIONS_ASSIGNMENTS_RESET",
  LOCATIONS_ASSIGNMENTS_START_LOADING: "LOCATIONS_ASSIGNMENTS_START_LOADING",
  LOCATION_ADD: "LOCATION_ADD",
  LOCATIONS_ADD: "LOCATIONS_ADD",
  LOCATIONS_FINISH_LOADING: "LOCATIONS_FINISH_LOADING",
  ASSIGNMENTS_ADD: "ASSIGNMENTS_ADD",
  ASSIGNMENTS_FINISH_LOADING: "ASSIGNMENTS_FINISH_LOADING",
  LOCATIONS_ASSIGNMENTS_SET_MESSAGE: "LOCATIONS_ASSIGNMENTS_SET_MESSAGE",
  LOCATIONS_SET_SEARCH_TERM: "LOCATIONS_SET_SEARCH_TERM",
  ASSIGN_LOCATION: "ASSIGN_LOCATION",
  UNASSIGN_LOCATION: "UNASSIGN_LOCATION",
};

const mutations = {
  [t.LOCATIONS_ASSIGNMENTS_RESET](state) {
    Object.assign(state, getInitialState());
  },
  [t.LOCATIONS_ASSIGNMENTS_START_LOADING](state, { projectId, datasheetId }) {
    state.locationsIsLoading = true;
    state.assignmentsIsLoading = true;
    state.projectId = projectId;
    state.datasheetId = datasheetId;
    state.message = null;
    state.messageType = null;
    state.locationsObj = {};
    state.locationsTotal = null;
    state.assignmentsTotal = null;
  },
  [t.LOCATION_ADD](state, loc) {
    // single location created in modal
    if (loc["@id"]) {
      Vue.set(state.locationsObj, loc["@id"], {
        ...loc,
        ...state.locationsObj[loc["@id"]],
      });
    }
  },
  [t.LOCATIONS_ADD](state, data) {
    // multiple locations fetched from API
    data["hydra:member"].forEach((loc) => {
      if (loc["@id"]) {
        Vue.set(state.locationsObj, loc["@id"], {
          ...loc,
          ...state.locationsObj[loc["@id"]],
        });
      }
    });
    state.locationsTotal = data["hydra:totalItems"];
  },
  [t.LOCATIONS_FINISH_LOADING](state) {
    state.locationsIsLoading = false;
  },
  [t.ASSIGNMENTS_ADD](state, data) {
    let locationsTemp = state.locationsObj;
    data["hydra:member"].forEach((loc) => {
      if (loc.location && loc.location["@id"]) {
        Vue.set(state.locationsObj, loc.location["@id"], {
          ...state.locationsObj[loc.location["@id"]],
          "@id": loc.location["@id"],
          name: loc.location.name,
          lngLat: loc.location.lngLat,
          assignment: {
            "@id": loc["@id"],
            "@type": loc["@type"],
            datasheet: loc.datasheet,
            id: loc.id,
            project: loc.project,
          },
        });
      }
    });
    state.locationsObj = locationsTemp;
    state.assignmentsTotal = data["hydra:totalItems"];
  },
  [t.ASSIGNMENTS_FINISH_LOADING](state) {
    state.assignmentsIsLoading = false;
  },
  [t.LOCATIONS_ASSIGNMENTS_SET_MESSAGE](state, { message, messageType }) {
    state.message = message;
    state.messageType = messageType;
  },
  [t.LOCATIONS_SET_SEARCH_TERM](state, searchTerm) {
    state.searchTerm = searchTerm;
  },
  [t.ASSIGN_LOCATION](state, newAssignment) {
    Vue.set(
      state.locationsObj[newAssignment.location["@id"]],
      "assignment",
      newAssignment
    );
  },
  [t.UNASSIGN_LOCATION](state, locationId) {
    Vue.delete(state.locationsObj[locationId], "assignment");
  },
};

const actions = {
  async fetchDatasheetAssignmentsAndProjectLocations(
    { commit, dispatch, state },
    { projectId, datasheetId }
  ) {
    commit(t.LOCATIONS_ASSIGNMENTS_START_LOADING, { projectId, datasheetId });
    await dispatch("_fetchDatasheetAssignments");
    await dispatch("_fetchProjectLocations");
  },
  async _fetchProjectLocations({ commit, state }) {
    const itemsPerPage = 1000;
    let url = `/projects/${state.projectId}/locations?itemsPerPage=${itemsPerPage}&order[name]=asc&page=1`;

    while (url) {
      const response = await fetch(url);
      if (!response.ok) {
        const error = await response.text();
        commit(t.LOCATIONS_ASSIGNMENTS_SET_MESSAGE, {
          message: error,
          messageType: "danger",
        });
        break;
      }
      const data = await response.json();
      commit(t.LOCATIONS_ADD, data);
      if (data["hydra:view"] && data["hydra:view"]["hydra:next"]) {
        url = data["hydra:view"]["hydra:next"];
      } else {
        url = null;
        break;
      }
    }
    commit(t.LOCATIONS_FINISH_LOADING);
  },
  async _fetchDatasheetAssignments({ commit, dispatch, state }) {
    const itemsPerPage = 500;
    let url = `${state.datasheetId}/assigned_locations?itemsPerPage=${itemsPerPage}&exists[member]=false&order[location.name]=asc&page=1`;

    while (url) {
      const response = await fetch(url);
      if (!response.ok) {
        const error = await response.text();
        commit(t.LOCATIONS_ASSIGNMENTS_SET_MESSAGE, {
          message: error,
          messageType: "danger",
        });
        break;
      }
      const data = await response.json();
      commit(t.ASSIGNMENTS_ADD, data);
      if (data["hydra:view"] && data["hydra:view"]["hydra:next"]) {
        url = data["hydra:view"]["hydra:next"];
      } else {
        url = null;
        break;
      }
    }
    commit(t.ASSIGNMENTS_FINISH_LOADING);
  },
  async setSearchTerm({ commit }, searchTerm) {
    commit(t.LOCATIONS_SET_SEARCH_TERM, searchTerm);
  },
  async assignLocation({ commit, state }, locationId) {
    // Create a new assignment object for this location
    const assignment = {
      project: `/projects/${state.projectId}`,
      datasheet: state.datasheetId,
      location: locationId,
    };
    const response = await fetch("project_member_locations", {
      method: "POST",
      body: JSON.stringify(assignment),
    });
    if (!response.ok) {
      const error = await response.text();
      commit(t.LOCATIONS_ASSIGNMENTS_SET_MESSAGE, {
        message: error,
        messageType: "danger",
      });
      return;
    }
    const newAssignment = await response.json();
    commit(t.ASSIGN_LOCATION, newAssignment);
  },
  async unassignLocation({ commit }, location) {
    const response = await fetch(location.assignment["@id"], {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.text();
      commit(t.LOCATIONS_ASSIGNMENTS_SET_MESSAGE, {
        message: error,
        messageType: "danger",
      });
      return;
    }
    commit(t.UNASSIGN_LOCATION, location["@id"]);
  },
  async assignAllLocations({ dispatch, state, getters }) {
    for (const location of getters.searchFilteredLocations) {
      if (!location.assignment) {
        // only assign if not already assigned
        // eslint-disable-next-line no-await-in-loop
        await dispatch("assignLocation", location["@id"]);
      }
    }
  },
  async unassignAllLocations({ dispatch, state, getters }) {
    for (const location of getters.searchFilteredLocations) {
      if (location.assignment) {
        // only unassign if currently assigned
        // eslint-disable-next-line no-await-in-loop
        await dispatch("unassignLocation", location);
      }
    }
  },
  async createLocationAndAssign({ dispatch, state, commit }, location) {
    // First create the location
    console.log("test");
    const newLocationResponse = await fetch("/areas", {
      method: "POST",
      body: JSON.stringify({
        project: `/projects/${state.projectId}`,
        ...location,
      }),
    });

    if (!newLocationResponse.ok) {
      const error = await newLocationResponse.text();
      commit(t.LOCATIONS_ASSIGNMENTS_SET_MESSAGE, {
        message: error,
        messageType: "danger",
      });
      return;
    }
    const newLocation = await newLocationResponse.json();
    // Add the new location to the store
    commit(t.LOCATION_ADD, newLocation);
    // Now assign it to the datasheet
    await dispatch("assignLocation", newLocation["@id"]);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
